// Apolo IDE — SidePanel: Diff tab (Sprint v11 E3).
//
// Listens for `apolo:showDiff` custom events with payload
// { filePath, oldText, newText }. When fired, mounts <HunkDiff> so the
// user can accept/reject individual hunks. On Apply, persists via the
// Electron file IPC (no Worker round-trip — see DECISIONS-v11 D3).

import { useCallback, useEffect, useState } from 'react';
import { FileDiff } from 'lucide-react';
import HunkDiff from '../../code-ide/HunkDiff.jsx';
import { hasElectronBridge } from '../../../lib/electronBridge.js';

export default function DiffTab() {
  const [diff, setDiff] = useState(null); // { filePath, root, oldText, newText, mtime }
  const [status, setStatus] = useState({ phase: 'idle', error: null });

  useEffect(() => {
    function onShowDiff(e) {
      const payload = e.detail;
      if (!payload || typeof payload.oldText !== 'string' || typeof payload.newText !== 'string') {
        return;
      }
      setDiff({
        filePath: payload.filePath || 'untitled',
        root: payload.root || '',
        oldText: payload.oldText,
        newText: payload.newText,
        mtime: payload.mtime ?? null,
      });
      setStatus({ phase: 'idle', error: null });
    }
    window.addEventListener('apolo:showDiff', onShowDiff);
    return () => window.removeEventListener('apolo:showDiff', onShowDiff);
  }, []);

  const handleApply = useCallback(async (newText, acceptedIds) => {
    if (!diff) return;
    if (!hasElectronBridge() || !diff.root) {
      setStatus({ phase: 'error', error: 'Electron file bridge ausente — abra no app.' });
      return;
    }
    setStatus({ phase: 'saving', error: null });
    try {
      const res = await window.apolo.file.write({
        root: diff.root,
        filePath: diff.filePath,
        content: newText,
        expectedMtime: diff.mtime,
      });
      setStatus({ phase: 'done', error: null, applied: acceptedIds.length, mtime: res?.mtime });
      window.dispatchEvent(
        new CustomEvent('apolo:diffApplied', {
          detail: { filePath: diff.filePath, acceptedIds, mtime: res?.mtime },
        }),
      );
    } catch (err) {
      setStatus({ phase: 'error', error: err?.message || 'Falha ao salvar' });
    }
  }, [diff]);

  const handleReject = useCallback(() => {
    setDiff(null);
    setStatus({ phase: 'idle', error: null });
  }, []);

  if (!diff) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-4 py-6 text-center">
        <FileDiff size={20} className="text-apolo-textTertiary" aria-hidden="true" />
        <p className="max-w-xs text-sm text-apolo-textTertiary">
          Diffs aparecem aqui hunk a hunk para você aceitar/rejeitar.
        </p>
        <p className="text-xs text-apolo-textTertiary">
          Dispare <code className="rounded bg-apolo-surface2 px-1 py-0.5">window.dispatchEvent(new CustomEvent(&apos;apolo:showDiff&apos;, &#123; detail &#125;))</code>
          {' '}para popular este painel.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {status.phase === 'error' && (
        <div role="alert" className="border-b border-apolo-error/30 bg-apolo-errorBg px-3 py-2 text-xs text-apolo-error">
          {status.error}
        </div>
      )}
      {status.phase === 'done' && (
        <div role="status" className="border-b border-apolo-success/30 bg-apolo-successBg px-3 py-2 text-xs text-apolo-success">
          {status.applied} hunk(s) aplicados em {diff.filePath}
        </div>
      )}
      <HunkDiff
        filePath={diff.filePath}
        oldText={diff.oldText}
        newText={diff.newText}
        onApply={handleApply}
        onReject={handleReject}
      />
    </div>
  );
}
